<template>
    <div class="form-horizontal">
        <div class="modal_content">
            <div class="alert alert_default">
                <div class="row m-b-10">
                    <div class="col-xs-4">
                        <h4 class="m-0">{{ quotation.total_products + quotation.total_discounts + quotation.total_taxes | pmlFormatCurrencyById(tenant.currency_id) }}</h4>
                        <small class="show" v-if="tenant.currency_id !== quotation.currency_id">
                            ({{ (quotation.total_products + quotation.total_discounts + quotation.total_taxes) * quotation.exchange_rate | pmlFormatCurrencyById(quotation.currency_id) }})
                        </small>
                    </div>
                    <div class="col-xs-4 t_center">
                        <div v-if="quotation.number">
                            <small class="c_default bold">{{ $t('quotation') }} {{ $t('number') }}:</small>
                            <label class="label label-inverse">{{ `#${quotation.number}` }}</label>
                        </div>
                    </div>
                    <div class="col-xs-4 t_center">
                        <small class="c_default bold">{{ $t('status') }}:</small>
                        <label class="label" :class="getStatusLabel(quotation.status)">{{ $t(quotation.status) }}</label>
                    </div>
                </div>
                <div class="row">
                    <div class="col-xs-10">
                        <strong>{{ $t('link') }}:</strong>
                        {{ quotationLink }}
                    </div>
                    <div class="col-xs-2">
                        <a :href="`/my-quotation/${quotation.code}`" target="_blank" class="btn btn-primary btn-xs m-0 pull-right" v-pml-tooltip="$t('show')"><i class="md md-visibility"></i></a>
                    </div>
                </div>
            </div>
            <div class="row">
                <div class="col-sm-8 m_bottom_15" :class="{'has-error': errors['template_id']}">
                    <label>{{ $t('template') }}</label>
                    <pml-select v-model="record.template_id" :options="templates" label="title"></pml-select>
                    <span class="error" v-if="errors['template_id']">{{ errors['template_id'][0] }}</span>
                </div>
                <div class="col-sm-4 m_bottom_15 t_right m-t-25">
                    <span class="btn btn-white btn-sm" @click="toggleCC()" :class="{ 'active': showCC }" v-pml-tooltip="$t('carbon_copy')">{{ $t('cc') }}</span>
                    <span class="btn btn-white btn-sm" @click="toggleBCC()" :class="{ 'active': showBCC }" v-pml-tooltip="$t('blind_carbon_copy')">{{ $t('bcc') }}</span>
                </div>
            </div>
            <div class="row">
                <div class="col-sm-12 m_bottom_15" :class="{'has-error': errors['subject']}">
                    <label>{{ $t('subject') }}</label>
                    <input type="text" class="form-control" v-model="record.subject">
                    <span class="error" v-if="errors['subject']">{{ errors['subject'][0] }}</span>
                </div>
            </div>
            <div class="row" v-if="showCC">
                <div class="col-sm-12 m_bottom_15" :class="{'has-error': errors['cc']}">
                    <label>{{ $t('cc') }}</label>
                    <pml-autocomplete-tag-input v-model="record.cc" :unfilteredTags="record.cc" :type="'email'" v-pml-autocomplete-tag-input-focus  :options="emailAddresses"></pml-autocomplete-tag-input>
                    <span class="error" v-if="errors['cc']">{{ errors['cc'][0] }}</span>
                </div>
            </div>
            <div class="row" v-if="showBCC">
                <div class="col-sm-12 m_bottom_15" :class="{'has-error': errors['bcc']}">
                    <label>{{ $t('bcc') }}</label>
                    <pml-autocomplete-tag-input v-model="record.bcc" :unfilteredTags="record.bcc" :type="'email'" v-pml-autocomplete-tag-input-focus  :options="emailAddresses"></pml-autocomplete-tag-input>
                    <span class="error" v-if="errors['bcc']">{{ errors['bcc'][0] }}</span>
                </div>
            </div>
            <div class="row">
                <div class="col-sm-12 m_bottom_15" :class="{'has-error': errors['body']}">
                    <label>{{ $t('message') }}</label>
                    <pml-summernote v-model="record.body" :value="record.body" :show-deal-btn="false"></pml-summernote>
                    <span class="error" v-if="errors['body']">{{ errors['body'][0] }}</span>
                </div>
            </div>
        </div>
        <div class="p-20 p-t-0 t_right">
            <span class="pull-left btn btn-inverse" @click.prevent="markAsSent" v-if="quotation.status !== 'sent'">{{ $t('mark_as_sent') }}</span>
            <router-link :to="{ name: 'deals.show.quotations', params: { id: deal.id } }" class="btn btn-white">{{ $t('cancel') }}</router-link>
            <button @click.prevent="askToSend()" class="btn btn-default" v-pml-block="isSaving">{{ $t('send') }}</button>
        </div>
    </div>
</template>

<script>
    import Notify from '@/utils/helpers/notify'
    import { trans } from '@/utils/helpers/translation'
    import { replace, signature } from '@/services/emails'
    import { send, changeStatus } from '@/services/quotations'
    import { quotationStatus } from '@/utils/helpers/label-colorify'
    import { find, isNull } from 'lodash'
    import { mapGetters } from 'vuex'
    import { sendEmail } from '@/utils/helpers/sweet-alert'

    export default {
        name: 'SendQuotation',
        data () {
            return {
                record: {
                    cc: '',
                    bcc: '',
                    body: '',
                    template_id: ''
                },
                errors: [],
                showCC: false,
                showBCC: false,
                isLoading: false,
                isSaving: false
            }
        },
        created () {
            this.clear()
        },
        computed: {
            ...mapGetters({
                'member': 'getMember',
                'tenant': 'getTenant',
                'templates': 'getTemplates',
                'deal': 'getDealInfo',
                'quotations': 'getDealQuotations',
                'emailAddresses': 'getEmailAddresses'
            }),
            quotation () {
                return find(this.quotations, { id: parseInt(this.$route.params.quotation) }) || {}
            },
            quotationLink () {
                return `${process.env.URL_LOCAL}/my-quotation/${this.quotation.code}`
            }
        },
        watch: {
            'record.template_id' (val, old) {
                if (isNull(val) || val === '') {
                    this.clear()
                } else if (val !== old) {
                    this.isLoading = true

                    replace(this.record.template_id, this.deal.id)
                        .then(response => {
                            this.record.subject = response.data.subject
                            this.record.body = response.data.body
                        })
                        .catch(() => Notify.error(trans('_notifications.error')))
                        .then(() => { this.isLoading = false })
                }
            }
        },
        methods: {
            clear () {
                this.record.subject = ''
                this.record.body = ''
                if (this.member.active_email_signature)
                    signature(this.deal.id)
                        .then(response => {
                            this.record.body = response.data.body
                        })
            },
            getStatusLabel: (status) => quotationStatus(status),
            askToSend () {
                sendEmail()
                    .then((response) => (response.value) ? this.store() : null)
            },
            store () {
                this.isSaving = true

                send(this.deal.id, this.quotation.id, this.record)
                    .then(() => {
                        Notify.success(trans('_notifications.success'))
                        this.$router.push({ name: 'deals.show.quotations', params: { id: this.deal.id } })
                    })
                    .catch(error => { this.errors = error.response.data.errors || [] })
                    .then(() => { this.isSaving = false })
            },
            toggleCC () {
                this.showCC = !this.showCC
                this.record.cc = null
            },
            toggleBCC () {
                this.showBCC = !this.showBCC
                this.record.bcc = null
            },
            markAsSent () {
                changeStatus(this.quotation.deal_id, this.quotation.id, 'sent')
                    .then(() => {
                        Notify.success(trans('_notifications.success'))
                        this.$router.push({ name: 'deals.show.quotations', params: { id: this.deal.id } })
                    })
                    .catch(() => Notify.error(trans('_notifications.error')))
            }
        }
    }
</script>



// WEBPACK FOOTER //
// src/modules/deals/components/show/quotations/Send.vue